import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, Search } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AnomalyHistoryChart } from '@/features/objects/components/AnomalyHistoryChart'
import { AnalyticsHistoryChart } from '@/features/objects/components/AnalyticsHistoryChart'

interface ObjectOption {
    objectID: string
    objectName?: string
    objectDescription?: string
}

export function ObjectViewTab() {
    const { t } = useTranslation()
    const [selected, setSelected] = useState<string>('')
    const [search, setSearch] = useState('')

    const { data: objects = [], isLoading } = useQuery({
        queryKey: ['analytics', 'objects-list'],
        queryFn: async () => {
            const res = await api.get('/api/objects', { params: { limit: 500 } })
            return (res.data?.data || res.data || []) as ObjectOption[]
        },
    })

    const filtered = useMemo(() => {
        if (!search) return objects
        const q = search.toLowerCase()
        return objects.filter(
            (o) =>
                o.objectID.toLowerCase().includes(q) ||
                o.objectName?.toLowerCase().includes(q) ||
                o.objectDescription?.toLowerCase().includes(q)
        )
    }, [objects, search])

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('analytics.objectView', 'Object View')}</CardTitle>
            </CardHeader>
            <CardContent>
                {/* Object selector */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search objects..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                    <Select value={selected} onValueChange={setSelected} disabled={isLoading}>
                        <SelectTrigger className="w-full sm:w-80">
                            <SelectValue placeholder={isLoading ? 'Loading objects…' : 'Select an object'} />
                        </SelectTrigger>
                        <SelectContent>
                            {filtered.map((o) => (
                                <SelectItem key={o.objectID} value={o.objectID}>
                                    {o.objectID}{o.objectName ? ` — ${o.objectName}` : ''}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Chart */}
                {isLoading ? (
                    <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading objects...
                    </div>
                ) : !selected ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        Select an object above to view its anomaly history.
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <AnomalyHistoryChart objectID={selected} embedded />
                        <AnalyticsHistoryChart objectID={selected} embedded />
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
